import React, { useState } from 'react';
import { FadeIn } from './ui/FadeIn';
import { CtaButton } from './ui/CtaButton';
import { X, Gift, CheckCircle2, ChevronDown, FileText, Zap } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
function LegalModal({
  isOpen,
  onClose,
  title,
  children
}: {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
}) {
  return <AnimatePresence data-id="element-114">
      {isOpen && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.2
    }} className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={onClose} data-id="element-115">
          <motion.div initial={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} transition={{
        duration: 0.25
      }} className="bg-brand-card border border-brand-gold/20 rounded-xl w-full max-w-2xl max-h-[80vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()} data-id="element-116">
            <div className="flex items-center justify-between p-6 border-b border-brand-bg" data-id="element-117">
              <h3 className="text-xl font-bold text-white" data-id="element-118">{title}</h3>
              <button onClick={onClose} className="text-brand-muted hover:text-white transition-colors p-1" data-id="element-119">
                <X className="w-5 h-5" data-id="element-120" />
              </button>
            </div>
            <div className="p-6 overflow-y-auto text-brand-muted text-sm leading-relaxed space-y-4" data-id="element-121">
              {children}
            </div>
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
}
export function FinalCtaSection() {
  const [activeModal, setActiveModal] = useState<'privacy' | 'terms' | null>(null);
  const [showBonusDetails, setShowBonusDetails] = useState(false);
  const bonuses = [{
    id: 1,
    title: 'BONO #1 — KIT "CERO FRICCIÓN"',
    subtitle: 'Configura tus 4 cuentas en 30 minutos',
    image: 'https://cdn.magicpatterns.com/uploads/4syHH1GikufoyyhnLvaSb6/Bono_1.png',
    description: 'La mayoría compra el manual… y se atasca en el setup. No porque sea difícil. Sino porque no saben por dónde empezar. Este kit te quita esa fricción.',
    benefit: 'Te da nombres exactos, reglas copy/paste y un checklist paso a paso para que en 30 minutos dejes tus cuentas separadas, con funciones claras y listas para trabajar.',
    includes: ['Guía "Setup 30-min" (PDF ejecutable)', '3 rutas según tu situación (2, 3 o 4 cuentas)', 'Nombres recomendados (copy/paste directo a tu banco)', 'Plantilla de reglas rápidas (entrada/salida/movimiento)', 'Los 7 errores típicos del setup (y cómo evitarlos)', 'Checklist final imprimible'],
    forWho: 'Si te da flojera "pensar mucho" y solo quieres dejarlo listo hoy, este bono te ahorra horas de parálisis.'
  }, {
    id: 2,
    title: 'BONO #2 — GUÍA "ANTI-MEZCLA"',
    subtitle: 'Reglas de transferencias y pagos automáticos',
    image: 'https://cdn.magicpatterns.com/uploads/9ScnV6eAsvuUmvFq6MMLUS/bono_2.png',
    description: 'El sistema funciona cuando tú estás motivado. Pero se rompe cuando estás cansado, ocupado o ansioso. Por eso este bono automatiza el orden.',
    benefit: 'Te enseña qué transferir, cuándo, con qué frecuencia y bajo qué reglas para que tu dinero se separe sin que tengas que recordarlo.',
    includes: ['Guía "Automatizaciones Anti-Mezcla" (PDF)', '3 modelos de frecuencia (según tu tipo de ingreso)', '12 reglas "si-entonces" listas para copiar', 'Mapa de pagos automáticos (qué cuenta paga qué)', 'Plantilla de calendario mensual/quincenal', 'Checklist de configuración (1 tarde, listo)'],
    forWho: 'Si eres indisciplinado, si tus ingresos son variables, o si simplemente odias "estar encima" de tu dinero todo el tiempo.'
  }, {
    id: 3,
    title: 'BONO #3 — WORKBOOK "ANSIEDAD → ACCIÓN"',
    subtitle: '10 minutos para volver al control',
    image: 'https://cdn.magicpatterns.com/uploads/sYTRNwTF1ofk4DcEngUhDX/bono_3.png',
    description: 'Aquí está el punto donde la gente pierde dinero: Cuando sienten urgencia, mueven plata para calmarse. Y después se arrepienten.',
    benefit: 'Este workbook te da un protocolo de 10 minutos para frenar el impulso, bajar la activación y tomar la decisión correcta (no la que te calma 5 minutos).',
    includes: ['Workbook rellenable "Ansiedad → Acción" (PDF)', 'Reset de 90 segundos (respiración + postura + enfoque)', 'Plantilla "De emoción a número" (para ver qué te asusta realmente)', 'Decisión en 3 preguntas (función / tiempo / movimiento mínimo)', 'Lista de acciones permitidas (y prohibidas) bajo ansiedad', 'Tracker de impulsos 7 días (para descubrir tu patrón)', 'Tarjeta móvil "Pausa antes de mover" (versión de bolsillo)'],
    forWho: 'Si las noticias te activan, si tiendes a "invertir para recuperar", o si compras para calmarte… esto te salva.'
  }];
  const getCheckoutUrl = () => {
    const currentParams = new URLSearchParams(window.location.search);
    const utmKeys = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'];
    const checkoutParams = new URLSearchParams();
    utmKeys.forEach(key => {
      const value = currentParams.get(key);
      if (value) {
        checkoutParams.set(key, value);
      }
    });
    const base = 'https://pay.hotmart.com/F104344877Y';
    const queryString = checkoutParams.toString();
    return queryString ? `${base}?${queryString}` : base;
  };
  return <section className="w-full px-4 md:px-6 pb-8" data-id="element-122">
      <div className="max-w-3xl mx-auto" data-id="element-123">
        <FadeIn data-id="element-124">
          <div className="bg-gradient-to-b from-brand-card to-brand-bg border border-brand-gold rounded-2xl p-8 md:p-16 text-center shadow-2xl shadow-brand-gold/5 relative overflow-hidden" id="final-cta" data-id="element-125">
            {/* Background decorative glow */}
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-32 bg-brand-gold/10 blur-[100px] pointer-events-none" data-id="element-126"></div>

            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6 font-serif tracking-tight" data-id="element-127">
              MANUAL DEL DINERO
            </h2>

            <div className="flex items-center justify-center gap-4 mb-8" data-id="element-128">
              <span className="text-2xl md:text-3xl text-brand-muted line-through" data-id="element-129">
                US$97
              </span>
              <span className="text-5xl md:text-6xl font-bold text-brand-gold" data-id="element-130">
                US$6.39
              </span>
              <span className="bg-brand-gold text-brand-bg text-sm font-bold px-3 py-1.5 rounded-md" data-id="element-131">
                93% OFF
              </span>
            </div>

            <div className="space-y-2 text-brand-muted mb-10 text-lg" data-id="element-132">
              <p data-id="element-133">Acceso inmediato</p>
              <p data-id="element-134">
                Formato: ebook + plantillas + checklists + plan accionable +
                Bonos GRATIS
              </p>
            </div>

            {/* Bonuses summary */}
            <div className="bg-brand-bg/60 border border-brand-gold/15 rounded-xl p-6 mb-10 text-left max-w-md mx-auto" data-id="element-135">
              <div className="flex items-center gap-2 mb-4 justify-center" data-id="element-136">
                <Gift className="w-4 h-4 text-brand-gold" data-id="element-137" />
                <span className="text-sm font-bold text-brand-gold uppercase tracking-wide" data-id="element-138">
                  Bonos incluidos gratis (Valor $47 USD)
                </span>
              </div>
              <ul className="space-y-3" data-id="element-139">
                <li className="flex items-start gap-2.5 text-sm text-white" data-id="element-140">
                  <CheckCircle2 className="w-4 h-4 text-brand-gold mt-0.5 shrink-0" data-id="element-141" />
                  <span data-id="element-142">
                    Kit "Cero Fricción"{' '}
                    <span className="text-brand-muted" data-id="element-143">— Setup en 30 min</span>
                  </span>
                </li>
                <li className="flex items-start gap-2.5 text-sm text-white" data-id="element-144">
                  <CheckCircle2 className="w-4 h-4 text-brand-gold mt-0.5 shrink-0" data-id="element-145" />
                  <span data-id="element-146">
                    Guía "Anti-Mezcla"{' '}
                    <span className="text-brand-muted" data-id="element-147">
                      — Pagos automáticos
                    </span>
                  </span>
                </li>
                <li className="flex items-start gap-2.5 text-sm text-white" data-id="element-148">
                  <CheckCircle2 className="w-4 h-4 text-brand-gold mt-0.5 shrink-0" data-id="element-149" />
                  <span data-id="element-150">
                    Workbook "Ansiedad → Acción"{' '}
                    <span className="text-brand-muted" data-id="element-151">
                      — Control en 10 min
                    </span>
                  </span>
                </li>
              </ul>

              {/* Leer más toggle */}
              <button onClick={() => setShowBonusDetails(!showBonusDetails)} className="mt-4 w-full flex items-center justify-center gap-1.5 text-brand-gold text-sm font-medium hover:text-brand-gold-dark transition-colors py-2" data-id="element-152">
                <span data-id="element-153">{showBonusDetails ? 'Leer menos' : 'Leer más'}</span>
                <motion.div animate={{
                rotate: showBonusDetails ? 180 : 0
              }} transition={{
                duration: 0.3
              }} data-id="element-154">
                  <ChevronDown className="w-4 h-4" data-id="element-155" />
                </motion.div>
              </button>

              {/* Expandable bonus details */}
              <AnimatePresence data-id="element-156">
                {showBonusDetails && <motion.div initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} transition={{
                duration: 0.4,
                ease: 'easeInOut'
              }} className="overflow-hidden" data-id="element-157">
                    <div className="pt-4 space-y-6 border-t border-brand-gold/10 mt-2" data-id="element-158">
                      {bonuses.map((bono, index) => <div key={bono.id} className="bg-brand-bg/40 border border-brand-gold/10 rounded-xl overflow-hidden" data-id="element-159">
                          {/* Bonus image */}
                          <div className="relative h-48 overflow-hidden bg-brand-bg/50 flex items-center justify-center" data-id="element-160">
                            <img src={bono.image} alt={bono.title} className={`w-full h-full ${index !== 0 ? 'object-cover object-top' : 'object-cover'}`} data-id="element-161" />
                            <div className="absolute top-3 left-3 bg-brand-gold text-brand-bg font-bold text-xs px-2.5 py-1 rounded shadow-lg" data-id="element-162">
                              HOY: GRATIS
                            </div>
                          </div>

                          {/* Bonus content */}
                          <div className="p-5 space-y-4" data-id="element-163">
                            <div data-id="element-164">
                              <h4 className="text-base font-bold text-white mb-0.5" data-id="element-165">
                                {bono.title}
                              </h4>
                              <p className="text-brand-gold text-sm font-medium" data-id="element-166">
                                {bono.subtitle}
                              </p>
                            </div>

                            <p className="text-brand-text/90 text-sm leading-relaxed" data-id="element-167">
                              {bono.description}
                            </p>
                            <p className="text-brand-text/90 text-sm leading-relaxed font-medium" data-id="element-168">
                              {bono.benefit}
                            </p>

                            <div className="bg-brand-bg/50 rounded-lg p-4 border border-brand-gold/10" data-id="element-169">
                              <h5 className="text-white font-bold text-xs uppercase tracking-wide mb-2 flex items-center gap-1.5" data-id="element-170">
                                <FileText className="w-3.5 h-3.5 text-brand-gold" data-id="element-171" />
                                Lo que incluye:
                              </h5>
                              <ul className="space-y-1.5" data-id="element-172">
                                {bono.includes.map((item, i) => <li key={i} className="flex items-start gap-2 text-xs text-brand-muted" data-id="element-173">
                                    <CheckCircle2 className="w-3.5 h-3.5 text-brand-gold/70 mt-0.5 shrink-0" data-id="element-174" />
                                    <span data-id="element-175">{item}</span>
                                  </li>)}
                              </ul>
                            </div>

                            <div className="flex items-start gap-2 text-xs italic text-brand-muted bg-brand-gold/5 p-3 rounded-lg border-l-2 border-brand-gold" data-id="element-176">
                              <Zap className="w-3.5 h-3.5 text-brand-gold mt-0.5 shrink-0" data-id="element-177" />
                              <p data-id="element-178">
                                <strong className="text-brand-gold not-italic" data-id="element-179">
                                  Para quién:
                                </strong>{' '}
                                {bono.forWho}
                              </p>
                            </div>
                          </div>
                        </div>)}
                    </div>
                  </motion.div>}
              </AnimatePresence>
            </div>

            <div className="max-w-md mx-auto mb-8" data-id="element-180">
              <CtaButton fullWidth onClick={() => {
              if (window.fbq) {
                window.fbq('track', 'AddToCart', {
                  content_name: 'Manual del Dinero',
                  content_type: 'product',
                  value: 6.39,
                  currency: 'USD'
                });
              }
              window.open(getCheckoutUrl(), '_blank');
            }} data-id="element-181">
                Compra ahora
                <br data-id="element-182" />y ejecuta el Día 1 hoy
              </CtaButton>
            </div>

            <p className="text-brand-text/80 italic text-sm md:text-base" data-id="element-183">
              No "cuando tengas tiempo". <br data-id="element-184" />
              Porque el desorden siempre cobra primero.
            </p>
          </div>

          <div className="mt-12 text-center text-brand-muted text-xs space-y-3" data-id="element-185">
            <div className="flex items-center justify-center gap-3" data-id="element-186">
              <button onClick={() => setActiveModal('privacy')} className="underline underline-offset-2 hover:text-brand-gold transition-colors" data-id="element-187">
                Política de Privacidad
              </button>
              <span className="text-brand-muted/40" data-id="element-188">·</span>
              <button onClick={() => setActiveModal('terms')} className="underline underline-offset-2 hover:text-brand-gold transition-colors" data-id="element-189">
                Términos y Condiciones
              </button>
            </div>
            <p data-id="element-190">
              © {new Date().getFullYear()} Manual del Dinero. Todos los
              derechos reservados.
            </p>
          </div>
        </FadeIn>
      </div>

      {/* Privacy Policy Modal */}
      <LegalModal isOpen={activeModal === 'privacy'} onClose={() => setActiveModal(null)} title="Política de Privacidad" data-id="element-191">
        <p data-id="element-192">
          <strong className="text-white" data-id="element-193">Última actualización:</strong> Febrero
          2026
        </p>
        <p data-id="element-194">
          En Manual del Dinero, respetamos tu privacidad y nos comprometemos a
          proteger tu información personal.
        </p>
        <p data-id="element-195">
          <strong className="text-white" data-id="element-196">1. Información que recopilamos</strong>
        </p>
        <p data-id="element-197">
          Recopilamos únicamente la información necesaria para procesar tu
          compra: nombre, correo electrónico y datos de pago. No almacenamos
          información de tarjetas de crédito directamente; esta es procesada por
          nuestro proveedor de pagos seguro.
        </p>
        <p data-id="element-198">
          <strong className="text-white" data-id="element-199">2. Uso de la información</strong>
        </p>
        <p data-id="element-200">
          Tu información se utiliza exclusivamente para: entregar el producto
          digital adquirido, enviar actualizaciones relacionadas con tu compra,
          y mejorar nuestros servicios.
        </p>
        <p data-id="element-201">
          <strong className="text-white" data-id="element-202">3. Compartir información</strong>
        </p>
        <p data-id="element-203">
          No vendemos, alquilamos ni compartimos tu información personal con
          terceros, excepto cuando sea necesario para procesar tu compra o
          cuando la ley lo requiera.
        </p>
        <p data-id="element-204">
          <strong className="text-white" data-id="element-205">4. Seguridad</strong>
        </p>
        <p data-id="element-206">
          Implementamos medidas de seguridad técnicas y organizativas para
          proteger tu información contra acceso no autorizado, alteración o
          destrucción.
        </p>
        <p data-id="element-207">
          <strong className="text-white" data-id="element-208">5. Tus derechos</strong>
        </p>
        <p data-id="element-209">
          Puedes solicitar acceso, corrección o eliminación de tus datos
          personales en cualquier momento contactándonos directamente.
        </p>
        <p data-id="element-210">
          <strong className="text-white" data-id="element-211">6. Contacto</strong>
        </p>
        <p data-id="element-212">
          Para cualquier consulta sobre privacidad, puedes escribirnos y te
          responderemos en un plazo máximo de 48 horas.
        </p>
      </LegalModal>

      {/* Terms & Conditions Modal */}
      <LegalModal isOpen={activeModal === 'terms'} onClose={() => setActiveModal(null)} title="Términos y Condiciones" data-id="element-213">
        <p data-id="element-214">
          <strong className="text-white" data-id="element-215">Última actualización:</strong> Febrero
          2026
        </p>
        <p data-id="element-216">
          Al adquirir el Manual del Dinero, aceptas los siguientes términos y
          condiciones.
        </p>
        <p data-id="element-217">
          <strong className="text-white" data-id="element-218">1. Producto digital</strong>
        </p>
        <p data-id="element-219">
          El Manual del Dinero es un producto digital (ebook) que incluye
          plantillas, checklists y un plan accionable. Se entrega en formato
          digital inmediatamente después de la compra.
        </p>
        <p data-id="element-220">
          <strong className="text-white" data-id="element-221">2. Uso personal</strong>
        </p>
        <p data-id="element-222">
          El contenido adquirido es para uso personal y no puede ser
          redistribuido, revendido, compartido públicamente ni reproducido sin
          autorización expresa.
        </p>
        <p data-id="element-223">
          <strong className="text-white" data-id="element-224">3. Propiedad intelectual</strong>
        </p>
        <p data-id="element-225">
          Todo el contenido del Manual del Dinero, incluyendo textos,
          plantillas, gráficos y metodologías, está protegido por derechos de
          autor.
        </p>
        <p data-id="element-226">
          <strong className="text-white" data-id="element-227">4. Naturaleza educativa</strong>
        </p>
        <p data-id="element-228">
          Este producto tiene fines educativos e informativos. No constituye
          asesoría financiera profesional. Las decisiones financieras son
          responsabilidad exclusiva del comprador.
        </p>
        <p data-id="element-229">
          <strong className="text-white" data-id="element-230">5. Política de reembolso</strong>
        </p>
        <p data-id="element-231">
          Dado que se trata de un producto digital con entrega inmediata, no se
          ofrecen reembolsos una vez realizada la descarga. Si tienes problemas
          técnicos con la entrega, contáctanos y lo resolveremos.
        </p>
        <p data-id="element-232">
          <strong className="text-white" data-id="element-233">6. Modificaciones</strong>
        </p>
        <p data-id="element-234">
          Nos reservamos el derecho de modificar estos términos. Las
          modificaciones serán efectivas desde su publicación.
        </p>
      </LegalModal>
    </section>;
}